
import { Plus, Trash2, GripVertical } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import type { ActivityFormData } from '../ActivityEditorPage.types';
import styles from './TypeSpecificFields.module.scss';

interface OrderingFieldsProps {
  items: string[];
  onChange: (updates: Partial<ActivityFormData>) => void;
  error?: string;
}

export function OrderingFields({ items, onChange, error }: OrderingFieldsProps) {
  const handleItemChange = (index: number, value: string) => {
    const newItems = [...items];
    newItems[index] = value;
    onChange({ items: newItems, correctOrder: newItems.map((_, i) => i) });
  };

  const handleAddItem = () => {
    const newItems = [...items, ''];
    onChange({ items: newItems, correctOrder: newItems.map((_, i) => i) });
  };

  const handleRemoveItem = (index: number) => {
    if (items.length <= 2) {
      return; // Minimum 2 items required
    }
    const newItems = items.filter((_, i) => i !== index);
    onChange({ items: newItems, correctOrder: newItems.map((_, i) => i) });
  };

  return (
    <div className={styles.typeFields}>
      <h3 className={styles.typeFields__title}>Elementos a ordenar</h3>
      <p className={styles.typeFields__description}>
        Añade los elementos en el orden correcto. Los estudiantes los verán desordenados y deberán ordenarlos. Añade al menos 2 elementos.
      </p>

      <div className={styles.typeFields__list}>
        {items.map((item, index) => (
          <div key={index} className={styles.typeFields__optionItem}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '40px' }}>
              <GripVertical size={16} style={{ color: '#9CA3AF' }} />
              <span style={{ color: '#6B7280' }}>{index + 1}.</span>
            </div>
            <div className={styles.typeFields__optionInput}>
              <Input
                value={item}
                onChange={(e) => handleItemChange(index, e.target.value)}
                placeholder={`Elemento ${index + 1}`}
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              leftIcon={<Trash2 />}
              onClick={() => handleRemoveItem(index)}
              disabled={items.length <= 2}
              aria-label={`Eliminar elemento ${index + 1}`}
            >
              <span className="sr-only">Eliminar</span>
            </Button>
          </div>
        ))}
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        leftIcon={<Plus />}
        onClick={handleAddItem}
        className={styles.typeFields__addButton}
      >
        Añadir elemento
      </Button>

      {error && <span className={styles.typeFields__error}>{error}</span>}

      <p className={styles.typeFields__hint}>
        💡 Consejo: El orden en que creas estos elementos es el orden correcto. Los estudiantes los verán desordenados.
      </p>
    </div>
  );
}
